import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, List, Tag, Typography, Spin, Alert, Divider, Space, message, Form } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import carePlanningService from '../services/carePlanningService';
import MomentDatePicker from './MomentDatePicker.tsx';
import { usePatientChart } from '../context/PatientChartContext';

// Import existing form components
import VitalsInputForm from './VitalsInputForm';
import StructuredPhysicalExamFormFull from './StructuredPhysicalExamFormFull';
import ComprehensiveBiometricsForm from './ComprehensiveBiometricsForm'; // New form for all biometrics
import LabInputForm from './LabInputForm';
import LabPdfImportModal from './LabPdfImportModal'; // For PDF import
import UnifiedLifestyleForm from './UnifiedLifestyleForm';

import EKGStudyInputForm from './EKGStudyInputForm';
import ChestXRStudyInputForm from './ChestXRStudyInputForm';
import ThyroidUSStudyInputForm from './ThyroidUSStudyInputForm';
import HeartUSStudyInputForm from './HeartUSStudyInputForm';
import CarotidUSStudyInputForm from './CarotidUSStudyInputForm';
import AbdominalUSStudyInputForm from './AbdominalUSStudyInputForm';

const { Text, Title } = Typography;

const SECTIONS_CONFIG = [
    { key: 'Vitals', title: 'Vitals', formType: 'VitalsInputForm' },
    { key: 'Biometrics', title: 'Biometrics', formType: 'ComprehensiveBiometricsForm' },
    { key: 'PhysicalExam', title: 'Physical Exam', formType: 'StructuredPhysicalExamFormFull' },
    { key: 'Laboratory', title: 'Laboratory Data', formType: 'LabInputForm' },
    { key: 'EKG', title: 'EKG', formType: 'EKGStudyInputForm' },
    { key: 'ChestXR', title: 'Chest X-Ray', formType: 'ChestXRStudyInputForm' },
    { key: 'ThyroidUS', title: 'Thyroid US', formType: 'ThyroidUSStudyInputForm' },
    { key: 'HeartUS', title: 'Heart US', formType: 'HeartUSStudyInputForm' },
    { key: 'CarotidUS', title: 'Carotid US', formType: 'CarotidUSStudyInputForm' },
    { key: 'AbdominalUS', title: 'Abdominal US', formType: 'AbdominalUSStudyInputForm' },
    { key: 'Lifestyle', title: 'Lifestyle Questionnaires', formType: 'LifestyleForms' },
];

const CarePlanningDataModal = ({ visible, onClose, patientId }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [statuses, setStatuses] = useState({});
    const [activeSectionKey, setActiveSectionKey] = useState(null);
    const [entryDate, setEntryDate] = useState(moment());
    const [editingData, setEditingData] = useState(null);
    const [ekgInitialData, setEkgInitialData] = useState(null);
    const [chestXRInitialData, setChestXRInitialData] = useState(null);
    const [thyroidUSInitialData, setThyroidUSInitialData] = useState(null);
    const [heartUSInitialData, setHeartUSInitialData] = useState(null);
    const [carotidUSInitialData, setCarotidUSInitialData] = useState(null);
    const [abdominalUSInitialData, setAbdominalUSInitialData] = useState(null);
    const [lifestyleInitialData, setLifestyleInitialData] = useState(null);
    const [vitalsForm] = Form.useForm();
    const [isCareLabPdfImportModalVisible, setIsCareLabPdfImportModalVisible] = useState(false);
    const { refreshVitals } = usePatientChart();


    const fetchStatuses = useCallback(async () => {
        if (!patientId || !visible) return;
        setLoading(true);
        setError(null);
        try {
            const response = await carePlanningService.getCarePlanningStatus(patientId);
            if (response.status === 'success') {
                setStatuses(response.data);
            } else {
                throw new Error(response.message || 'Failed to fetch statuses');
            }
        } catch (err) {
            setError(err.message || 'Could not load care planning statuses.');
            message.error(err.message || 'Could not load care planning statuses.');
        } finally {
            setLoading(false);
        }
    }, [patientId, visible]);

    useEffect(() => {
        if (visible) {
            fetchStatuses();
            setEntryDate(moment());
            setActiveSectionKey(null);

            setEditingData(null);
            vitalsForm.resetFields();
        }
    }, [visible, fetchStatuses, vitalsForm]);

    const handleOpenForm = async (sectionKey) => {
        setEditingData(null);
        setEkgInitialData(null);
        vitalsForm.resetFields();
        // Reset other forms if they have instances managed here

        if (statuses[sectionKey]?.status === 'Completed') {
            setLoading(true);
            try {
                if (sectionKey === 'EKG') {
                    // Special handling for EKG data retrieval
                    const ekgResponse = await carePlanningService.getEKGCarePlanningData(patientId);
                    if (ekgResponse.status === 'success' && ekgResponse.data) {
                        setEkgInitialData(ekgResponse.data);
                        console.log('EKG initial data set:', ekgResponse.data);
                    } else {
                        message.warn('No existing EKG data found. Opening a blank form.');
                    }
                } else if (sectionKey === 'ChestXR') {
                    // Special handling for Chest XR data retrieval
                    const chestXRResponse = await carePlanningService.getChestXRCarePlanningData(patientId);
                    if (chestXRResponse.status === 'success' && chestXRResponse.data) {
                        setChestXRInitialData(chestXRResponse.data);
                        console.log('Chest XR initial data set:', chestXRResponse.data);
                    } else {
                        message.warn('No existing Chest X-Ray data found. Opening a blank form.');
                    }
                } else if (sectionKey === 'ThyroidUS') {
                    // Special handling for Thyroid US data retrieval
                    const thyroidUSResponse = await carePlanningService.getThyroidUSCarePlanningData(patientId);
                    if (thyroidUSResponse.status === 'success' && thyroidUSResponse.data) {
                        setThyroidUSInitialData(thyroidUSResponse.data);
                        console.log('Thyroid US initial data set:', thyroidUSResponse.data);
                    } else {
                        message.warn('No existing Thyroid US data found. Opening a blank form.');
                    }
                } else if (sectionKey === 'HeartUS') {
                    // Special handling for Heart US data retrieval
                    const heartUSResponse = await carePlanningService.getHeartUSCarePlanningData(patientId);
                    if (heartUSResponse.status === 'success' && heartUSResponse.data) {
                        setHeartUSInitialData(heartUSResponse.data);
                        console.log('Heart US initial data set:', heartUSResponse.data);
                    } else {
                        message.warn('No existing Heart US data found. Opening a blank form.');
                    }
                } else if (sectionKey === 'CarotidUS') {
                    // Special handling for Carotid US data retrieval
                    const carotidUSResponse = await carePlanningService.getCarotidUSCarePlanningData(patientId);
                    if (carotidUSResponse.status === 'success' && carotidUSResponse.data) {
                        setCarotidUSInitialData(carotidUSResponse.data);
                        console.log('Carotid US initial data set:', carotidUSResponse.data);
                    } else {
                        message.warn('No existing Carotid US data found. Opening a blank form.');
                    }
                } else if (sectionKey === 'AbdominalUS') {
                    // Special handling for Abdominal US data retrieval
                    const abdominalUSResponse = await carePlanningService.getAbdominalUSCarePlanningData(patientId);
                    if (abdominalUSResponse.status === 'success' && abdominalUSResponse.data) {
                        setAbdominalUSInitialData(abdominalUSResponse.data);
                        console.log('Abdominal US initial data set:', abdominalUSResponse.data);
                    } else {
                        message.warn('No existing Abdominal US data found. Opening a blank form.');
                    }
                } else if (sectionKey === 'Lifestyle') {
                    // Special handling for Lifestyle data retrieval
                    try {
                        const lifestyleResponse = await axios.get(`/patients/${patientId}/flowsheet/latest-lifestyle-data`);
                        if (lifestyleResponse.data.status === 'success' && lifestyleResponse.data.data.formData) {
                            setLifestyleInitialData(lifestyleResponse.data.data.formData);
                            console.log('Lifestyle initial data set:', lifestyleResponse.data.data.formData);
                            if (lifestyleResponse.data.data.entryCount > 0) {
                                message.success(`Loaded ${lifestyleResponse.data.data.entryCount} lifestyle parameters from most recent entry.`);
                            }
                        } else {
                            setLifestyleInitialData({});
                            message.info('No existing lifestyle data found. Opening a blank form.');
                        }
                    } catch (err) {
                        console.error('Error fetching lifestyle data:', err);
                        setLifestyleInitialData({});
                        message.warn('Could not load existing lifestyle data. Opening a blank form.');
                    }
                } else {
                    // Regular data retrieval for other sections
                    const response = await carePlanningService.getCarePlanningSectionData(patientId, sectionKey);
                    if (response.status === 'success' && response.data) {
                        setEditingData(response.data);
                        if (sectionKey === 'Vitals' && response.data) {
                            vitalsForm.setFieldsValue(response.data);
                        }
                        // Other forms will use initialData prop if they manage their own instance or are simple enough
                    } else {
                        message.warn(`No existing data found for ${sectionKey} at this date/time. Opening a blank form.`);
                    }
                }
            } catch (err) {
                message.error(`Failed to fetch existing data for ${sectionKey}: ${err.message}`);
            } finally {
                setLoading(false);
            }
        }

        setActiveSectionKey(sectionKey);
    };

    const handleCloseForm = () => {
        setActiveSectionKey(null);
        setEditingData(null);
        setEkgInitialData(null);
        setChestXRInitialData(null);
        setThyroidUSInitialData(null);
        setHeartUSInitialData(null);
        setCarotidUSInitialData(null);
        setAbdominalUSInitialData(null);
        setLifestyleInitialData(null);
        vitalsForm.resetFields();
        fetchStatuses();
    };

    const handleSaveData = async (sectionKey, formData) => {
        if (!patientId || !entryDate) {
            message.error("Patient ID or Entry Date is missing.");
            return;
        }
        setLoading(true);
        setError(null);

        let dataToSubmit = formData;
        if (sectionKey === 'Biometrics') {
            dataToSubmit = { data: formData };
        }

        try {
            // Special handling for EKG section (with or without file)
            if (sectionKey === 'EKG') {
                console.log('Submitting EKG data:', formData);
                const result = await carePlanningService.submitEKGCarePlanningData(patientId, formData, entryDate);

                // Provide detailed feedback based on what was saved
                let successMessage = 'EKG data saved successfully!';
                if (result.data?.documentId) {
                    if (formData.documentType === 'link') {
                        successMessage += ' External link has been added to Documents.';
                    } else {
                        successMessage += ' File has been uploaded to Documents.';
                    }
                } else if (formData.file || formData.externalLink) {
                    successMessage += ' However, document upload is not available in this version. Please add the file manually to Documents.';
                }
                message.success(successMessage);
            } else if (sectionKey === 'ChestXR') {
                console.log('Submitting Chest XR data:', formData);
                const result = await carePlanningService.submitChestXRCarePlanningData(patientId, formData, entryDate);

                // Provide detailed feedback based on what was saved
                let successMessage = 'Chest X-Ray data saved successfully!';
                if (result.data?.documentId) {
                    if (formData.documentType === 'link') {
                        successMessage += ' External link has been added to Documents.';
                    } else {
                        successMessage += ' File has been uploaded to Documents.';
                    }
                } else if (formData.file || formData.externalLink) {
                    successMessage += ' However, document upload is not available in this version. Please add the file manually to Documents.';
                }
                message.success(successMessage);
            } else if (sectionKey === 'ThyroidUS') {
                console.log('Submitting Thyroid US data:', formData);
                const result = await carePlanningService.submitThyroidUSCarePlanningData(patientId, formData, entryDate);

                // Provide detailed feedback based on what was saved
                let successMessage = 'Thyroid US data saved successfully!';
                if (result.data?.documentId) {
                    if (formData.documentType === 'link') {
                        successMessage += ' External link has been added to Documents.';
                    } else {
                        successMessage += ' File has been uploaded to Documents.';
                    }
                } else if (formData.file || formData.externalLink) {
                    successMessage += ' However, document upload is not available in this version. Please add the file manually to Documents.';
                }
                message.success(successMessage);
            } else if (sectionKey === 'HeartUS') {
                console.log('Submitting Heart US data:', formData);
                const result = await carePlanningService.submitHeartUSCarePlanningData(patientId, formData, entryDate);

                // Provide detailed feedback based on what was saved
                let successMessage = 'Heart US data saved successfully!';
                if (result.data?.documentId) {
                    if (formData.documentType === 'link') {
                        successMessage += ' External link has been added to Documents.';
                    } else {
                        successMessage += ' File has been uploaded to Documents.';
                    }
                } else if (formData.file || formData.externalLink) {
                    successMessage += ' However, document upload is not available in this version. Please add the file manually to Documents.';
                }
                message.success(successMessage);
            } else if (sectionKey === 'CarotidUS') {
                console.log('Submitting Carotid US data:', formData);
                const result = await carePlanningService.submitCarotidUSCarePlanningData(patientId, formData, entryDate);

                // Provide detailed feedback based on what was saved
                let successMessage = 'Carotid US data saved successfully!';
                if (result.data?.documentId) {
                    if (formData.documentType === 'link') {
                        successMessage += ' External link has been added to Documents.';
                    } else {
                        successMessage += ' File has been uploaded to Documents.';
                    }
                } else if (formData.file || formData.externalLink) {
                    successMessage += ' However, document upload is not available in this version. Please add the file manually to Documents.';
                }
                message.success(successMessage);
            } else if (sectionKey === 'AbdominalUS') {
                console.log('Submitting Abdominal US data:', formData);
                const result = await carePlanningService.submitAbdominalUSCarePlanningData(patientId, formData, entryDate);

                // Provide detailed feedback based on what was saved
                let successMessage = 'Abdominal US data saved successfully!';
                if (result.data?.documentId) {
                    if (formData.documentType === 'link') {
                        successMessage += ' External link has been added to Documents.';
                    } else {
                        successMessage += ' File has been uploaded to Documents.';
                    }
                } else if (formData.file || formData.externalLink) {
                    successMessage += ' However, document upload is not available in this version. Please add the file manually to Documents.';
                }
                message.success(successMessage);
            } else {
                await carePlanningService.submitCarePlanningData(patientId, sectionKey, dataToSubmit, entryDate);
                message.success(`${SECTIONS_CONFIG.find(s=>s.key === sectionKey)?.title || sectionKey} data saved successfully!`);

                // Refresh vitals banner if vitals data was saved
                if (sectionKey === 'Vitals') {
                    refreshVitals();
                }
            }
            handleCloseForm();
        } catch (err) {
            console.error('Error saving care planning data:', err);
            console.error('Error response:', err.response?.data);
            console.error('Error status:', err.response?.status);
            const errorMessage = err.response?.data?.message || err.message || `Failed to save ${sectionKey} data.`;
            setError(errorMessage);
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };
    
    const renderSectionForm = () => {
        if (!activeSectionKey) return null;
        const sectionConfig = SECTIONS_CONFIG.find(s => s.key === activeSectionKey);
        if (!sectionConfig) return <Alert message="Selected section form not configured." type="error" />;

        const commonFormProps = {
            patientId,
            onCancel: handleCloseForm, // Individual forms can call this
            initialData: editingData, // Pass editingData as initialData
        };

        switch (sectionConfig.formType) {
            case 'VitalsInputForm':
                return <VitalsInputForm {...commonFormProps} form={vitalsForm} />;
            case 'ComprehensiveBiometricsForm':
                return <ComprehensiveBiometricsForm {...commonFormProps} onSave={(data) => handleSaveData(activeSectionKey, data)} />;
            case 'StructuredPhysicalExamFormFull':
                return <StructuredPhysicalExamFormFull {...commonFormProps} onSave={(examData) => handleSaveData(activeSectionKey, examData)} />;
            case 'LabInputForm':
                return (
                    <>
                        <Button 
                            icon={<UploadOutlined />} 
                            onClick={() => setIsCareLabPdfImportModalVisible(true)}
                            style={{ marginBottom: 16 }}
                        >
                            Import Lab Results from PDF
                        </Button>
                        <Divider>Or Enter Manually</Divider>
                        <LabInputForm 
                            {...commonFormProps} 
                            onSave={(data) => handleSaveData(activeSectionKey, data)} 
                            pendingOrders={[]} 
                            loadingPendingOrders={false} 
                        />
                    </>
                );

            case 'EKGStudyInputForm':
                return <EKGStudyInputForm {...commonFormProps} initialData={ekgInitialData} onSave={(data) => handleSaveData(activeSectionKey, data)} />;
            case 'ChestXRStudyInputForm':
                return <ChestXRStudyInputForm {...commonFormProps} initialData={chestXRInitialData} onSave={(data) => handleSaveData(activeSectionKey, data)} />;
            case 'ThyroidUSStudyInputForm':
                return <ThyroidUSStudyInputForm {...commonFormProps} initialData={thyroidUSInitialData} onSave={(data) => handleSaveData(activeSectionKey, data)} />;
            case 'HeartUSStudyInputForm':
                return <HeartUSStudyInputForm {...commonFormProps} initialData={heartUSInitialData} onSave={(data) => handleSaveData(activeSectionKey, data)} />;
            case 'CarotidUSStudyInputForm':
                return <CarotidUSStudyInputForm {...commonFormProps} initialData={carotidUSInitialData} onSave={(data) => handleSaveData(activeSectionKey, data)} />;
            case 'AbdominalUSStudyInputForm':
                return <AbdominalUSStudyInputForm {...commonFormProps} initialData={abdominalUSInitialData} onSave={(data) => handleSaveData(activeSectionKey, data)} />;
            case 'LifestyleForms':
                return (
                    <UnifiedLifestyleForm
                        {...commonFormProps}
                        initialData={lifestyleInitialData || {}}
                        onSave={(data) => handleSaveData(activeSectionKey, data)}
                    />
                );
            default:
                return <Alert message={`Form type "${sectionConfig.formType}" is not implemented.`} type="warning" />;
        }
    };

    return (
        <Modal
            title="Initial Care Planning Data Entry"
            open={visible} // AntD v5 uses 'open' instead of 'visible'
            onCancel={onClose}
            footer={null} 
            width={activeSectionKey ? (activeSectionKey === 'PhysicalExam' ? 1000 : (activeSectionKey === 'EKG' || activeSectionKey === 'ChestXR' || activeSectionKey === 'ThyroidUS' || activeSectionKey === 'HeartUS' || activeSectionKey === 'CarotidUS' ? 1000 : (activeSectionKey === 'AbdominalUS' ? 1200 : (activeSectionKey === 'Lifestyle' ? 1000 : 800)))) : 600}
            destroyOnClose
        >
            <Spin spinning={loading}>
                {error && <Alert message={error} type="error" showIcon closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}
                
                {!activeSectionKey ? (
                    <>
                        <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text>Set common entry date/time for this session:</Text>
                            <MomentDatePicker 
                                showTime={{ format: 'HH:mm' }} 
                                value={entryDate} 
                                onChange={date => setEntryDate(date || moment())} 
                                // style={{ width: '100%' }} // Let default width apply or adjust as needed
                            />
                        </Space>
                        <Divider />
                        <List
                            itemLayout="horizontal"
                            dataSource={SECTIONS_CONFIG}
                            renderItem={section => {
                                const statusInfo = statuses[section.key] || { status: 'Pending' };
                                let tagColor = 'default';
                                let statusText = statusInfo.status;
                                if (statusInfo.status === 'Pending') tagColor = 'orange';
                                if (statusInfo.status === 'Completed') {
                                    tagColor = 'green';
                                    statusText = `Completed`;
                                    if (statusInfo.lastUpdatedBy) {
                                        statusText += ` by ${statusInfo.lastUpdatedBy}`;
                                    }
                                    if (statusInfo.lastUpdatedAt) {
                                        statusText += ` on ${moment(statusInfo.lastUpdatedAt).format('YYYY-MM-DD HH:mm')}`;
                                    }
                                }
                                
                                return (
                                    <List.Item
                                        actions={[
                                            <Button type="link" onClick={() => handleOpenForm(section.key)}>
                                                {statusInfo.status === 'Completed' ? 'Edit Data' : 'Enter Data'}
                                            </Button>
                                        ]}
                                    >
                                        <List.Item.Meta
                                            title={<Text strong>{section.title}</Text>}
                                        />
                                        <Tag color={tagColor}>{statusText}</Tag>
                                    </List.Item>
                                );
                            }}
                        />
                        <Divider />
                        <Button onClick={onClose} style={{ marginTop: 16, float: 'right' }}>Close</Button>
                    </>
                ) : (
                    <>
                        <Title level={4}>{SECTIONS_CONFIG.find(s => s.key === activeSectionKey)?.title}</Title>
                        {renderSectionForm()}
                        <Space style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button onClick={handleCloseForm}>
                                Back to Sections
                            </Button>
                            {activeSectionKey === 'Vitals' && (
                                <Button
                                    type="primary"
                                    onClick={async () => {
                                        try {
                                            const values = await vitalsForm.validateFields();
                                            handleSaveData('Vitals', values);
                                        } catch (info) {
                                            console.log('Validate Failed for Vitals:', info);
                                            message.error('Please fill in all required Vitals fields correctly.');
                                        }
                                    }}
                                    loading={loading}
                                >
                                    Save Vitals
                                </Button>
                            )}
                            {/* ComprehensiveBiometricsForm and other forms with internal save buttons will call onSave directly */}
                        </Space>
                    </>
                )}
            </Spin>
            {isCareLabPdfImportModalVisible && patientId && (
                <LabPdfImportModal
                    patientId={patientId}
                    visible={isCareLabPdfImportModalVisible}
                    onClose={(importedSuccessfully, importedLabCollectionDate) => { // Handle success and collection date
                        setIsCareLabPdfImportModalVisible(false);
                        if (importedSuccessfully) {
                            // If LabPdfImportModal itself saves data and updates FlowsheetEntry:
                            // We just need to update the status for 'Laboratory' section.
                            // We can call handleSaveData with a special payload or make a dedicated status update call.
                            // For now, let's assume it saves and we just update status.
                            // A more robust way would be for LabPdfImportModal to return the data,
                            // then handleSaveData saves it and updates status.
                            // Let's try to make handleSaveData flexible.
                            // Lab PDF import saves directly to database, just trigger status update
                            handleSaveData('Laboratory', { pdfImportedSuccessfully: true });
                            message.success('Lab PDF imported. Section status will be updated.');
                        }
                    }}
                />
            )}
        </Modal>
    );
};

export default CarePlanningDataModal;
